import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import SEO from "@/components/SEO";

const HRTGuide = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="HRT Benefits and Risks: A Plain-English Guide"
        description="A clear, evidence-based guide to hormone replacement therapy (HRT) for menopause symptoms — the benefits, the risks, and the questions worth asking your doctor."
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "HRT Benefits and Risks: A Plain-English Guide",
          author: { "@type": "Organization", name: "Her Season" },
          publisher: {
            "@type": "Organization",
            name: "Her Season",
          },
          description:
            "A clear, evidence-based guide to hormone replacement therapy (HRT) for menopause symptoms.",
          mainEntityOfPage: "https://her-season.lovable.app/learning/hrt-guide",
        }}
      />

      <div className="container mx-auto max-w-3xl px-6 py-10">
        <Link
          to="/learning"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to learning
        </Link>
      </div>

      <article className="container mx-auto max-w-3xl px-6 pb-24 font-serif">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
          Guide · Evidence-based
        </p>
        <h1
          className="text-5xl md:text-6xl leading-[1.05] mb-8"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          HRT benefits and risks, in plain English.
        </h1>
        <p className="text-lg text-foreground/75 leading-relaxed mb-10">
          Hormone replacement therapy (often called HRT, or MHT — menopausal
          hormone therapy) is the most effective treatment we have for hot
          flushes, night sweats, and many other symptoms of perimenopause and
          menopause. It isn't the right choice for everyone — but for most
          healthy women under 60, or within ten years of their last period, the
          benefits usually outweigh the risks.
        </p>

        <section className="prose prose-lg max-w-none space-y-6">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What HRT actually does
          </h2>
          <p>
            HRT tops up the estrogen your ovaries are winding down from — and,
            if you still have a uterus, adds progesterone to protect the
            uterine lining. Some regimens also include testosterone for libido.
            It comes as patches, gels, sprays, tablets, and vaginal creams.
          </p>

          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            The benefits most women notice
          </h2>
          <ul>
            <li>Fewer and milder hot flushes and night sweats.</li>
            <li>Better sleep — often because the sweats stop waking you.</li>
            <li>Steadier mood and less brain fog for many women.</li>
            <li>
              Relief from vaginal dryness, painful sex, and recurrent urinary
              symptoms.
            </li>
            <li>Protection against bone loss and fractures long-term.</li>
          </ul>

          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            The risks, in perspective
          </h2>
          <p>
            The risks depend on your age, your health history, and the type of
            HRT. For most healthy women who start HRT under 60 or within ten
            years of their final period:
          </p>
          <ul>
            <li>
              <strong>Breast cancer:</strong> combined estrogen-plus-progestogen
              HRT slightly raises risk with longer use. Estrogen-only HRT
              (for women without a uterus) does not appear to raise breast
              cancer risk in most studies.
            </li>
            <li>
              <strong>Blood clots and stroke:</strong> tablets carry a small
              extra risk. Patches, gels, and sprays don't seem to raise clot
              risk in the same way.
            </li>
            <li>
              <strong>Heart disease:</strong> starting HRT early (under 60)
              is generally considered safe; starting many years after
              menopause is where the picture becomes less favourable.
            </li>
          </ul>

          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            When HRT is usually not recommended
          </h2>
          <p>
            A personal or strong family history of hormone-sensitive breast
            cancer, unexplained vaginal bleeding, untreated high blood
            pressure, active liver disease, or a recent blood clot are all
            reasons your doctor may suggest non-hormonal options instead.
          </p>

          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Questions worth asking your doctor
          </h2>
          <ul>
            <li>Which symptoms would HRT most likely help me with?</li>
            <li>Would a patch or gel suit me better than tablets?</li>
            <li>Do I need progesterone, and in what form?</li>
            <li>How will we know whether it's working?</li>
            <li>How long do you think I should stay on it?</li>
          </ul>

          <p className="text-sm text-muted-foreground italic">
            Her Season writes its own guides in plain English. We check them
            against trusted clinical sources — NICE NG23, the Australasian
            Menopause Society, The Menopause Society (formerly NAMS), and
            Jean Hailes — but the words here are ours. This is education, not
            medical advice; decisions about HRT belong with you and your
            clinician.
          </p>
        </section>

        <div className="mt-12">
          <Button asChild>
            <Link to="/symptom-explorer">Explore symptoms</Link>
          </Button>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default HRTGuide;
